import React, { useState, useMemo } from 'react';
import { BsGrid3X3GapFill, BsList, BsGrid, BsSearch, BsFilter, BsCashStack } from 'react-icons/bs';
import CajaCard from './index.jsx';
import './index.css';
import { getUser, formatCurrency } from '../../../utils/Common.js';
import Spinner from '../../Spinner';

const CajaCardContainer = ({ 
    cajas, 
    isLoading, 
    rotate,
    handleCashTransaction, 
    handleOpenCloseCash, 
    handleReloadCashStatus, 
    handleNewBalanceChange,
    newBalance,
    showAddWithdrawButtons, 
    roleBli,
    hasPendingRequest,
    pendingRequests = {},
    estafetasData = [],
    isLoadingEstafetas = false,
    handleGetEstafetasData
}) => {
    const user = getUser();
    const [viewType, setViewType] = useState('line'); 
    const [filters, setFilters] = useState({
        estafeta: '',
        caja: '',
        estado: 'todos'
    });

    // Filtrar cajas basado en los filtros
    const filteredCajas = useMemo(() => {
        return cajas.filter(caja => {
            const matchEstafeta = caja.NombreSucursal?.toLowerCase().includes(filters.estafeta.toLowerCase());
            const matchCaja = caja.NombreCaja?.toLowerCase().includes(filters.caja.toLowerCase());
            
            let matchEstado = true;
            if (filters.estado === 'abierta') {
                matchEstado = caja.EstatusCaja === 1;
            } else if (filters.estado === 'cerrada') {
                matchEstado = caja.EstatusCaja === 2;
            }

            return matchEstafeta && matchCaja && matchEstado;
        });
    }, [cajas, filters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            estafeta: '',
            caja: '',
            estado: 'todos'
        });
    };

    const renderViewControls = () => (
        <div className="dashboardCard view-controls mb-3">
            <div className="row align-items-center">
                {/* Filtros - Izquierda */}
                <div className="col-md-8">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2 mr-2">
                            <BsFilter size={14} className="text-muted" />
                            <span className="text-muted small">Filtros:</span>
                        </div>
                        
                        {/* Filtro por Estafeta */}
                        {user.roleId >= 4 && (
                        <div className="d-flex align-items-center gap-1">
                            <label className="small text-muted mb-0 mr-2">Estafeta:</label>
                            <div style={{
                                position: 'relative',
                                width: '150px',
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                border: '1px solid #0F4372',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px 8px'
                            }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        boxShadow: 'none',
                                        outline: 'none',
                                        fontWeight: 500,
                                        color: '#0F4372',
                                        paddingLeft: 0
                                    }}
                                    placeholder="Buscar estafeta..."
                                    value={filters.estafeta}
                                    onChange={(e) => handleFilterChange('estafeta', e.target.value)}
                                />
                            </div>
                        </div>
                        )}
                        {/* Filtro por Caja */}
                        <div className="d-flex align-items-center gap-1">
                            <label className="small text-muted mb-0 mr-2">Caja:</label>
                            <div style={{
                                position: 'relative',
                                width: '130px',
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                border: '1px solid #0F4372',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px 8px'
                            }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        boxShadow: 'none',
                                        outline: 'none',
                                        fontWeight: 500,
                                        color: '#0F4372',
                                        paddingLeft: 0
                                    }}
                                    placeholder="Buscar caja..."
                                    value={filters.caja}
                                    onChange={(e) => handleFilterChange('caja', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Filtro por Estado */}
                        <div className="d-flex align-items-center gap-1">
                            <label className="small text-muted mb-0 mr-3">Estado:</label>
                            <div style={{
                                position: 'relative',
                                width: '100px',
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(15,67,114,0.08)',
                                background: 'linear-gradient(90deg, #f8fafc 0%, #e9ecef 100%)',
                                border: '1px solid #0F4372',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px 8px'
                            }}>
                                <select
                                    className="form-control form-control-sm"
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        background: 'transparent',
                                        boxShadow: 'none',
                                        outline: 'none',
                                        fontWeight: 500,
                                        color: '#0F4372',
                                        paddingLeft: 0
                                    }}
                                    value={filters.estado}
                                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                                >
                                    <option value="todos">Todos</option>
                                    <option value="abierta">Abierta</option>
                                    <option value="cerrada">Cerrada</option>
                                </select>
                            </div>
                        </div>

                        {(filters.estafeta || filters.caja || filters.estado !== 'todos') && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={clearFilters}
                                title="Limpiar filtros"
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="d-flex align-items-center justify-content-end gap-2">
                        <span className="text-muted mr-2">Vista:</span>
                        <div className="btn-group" role="group">
                            <button
                                type="button"
                                className={`btn btn-sm ${viewType === 'line' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewType('line')}
                                title="Vista línea"
                            >
                                <BsList size={16} />
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${viewType === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewType('grid')}
                                title="Vista grid"
                            >
                                <BsGrid3X3GapFill size={16} />
                            </button>
                            <button
                                type="button"
                                className={`btn btn-sm ${viewType === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setViewType('table')}
                                title="Vista tabla"
                            >
                                <BsGrid size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mt-2">
                <div className="col-12">
                    <div className="d-flex align-items-center gap-2">
                        <BsSearch size={12} className="text-muted mr-2" />
                        <span className="small text-muted">
                            Mostrando {filteredCajas.length} de {cajas.length} cajas
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCajas = () => {
        if (viewType === 'table') {
            return (
                <div className="dashboardCard caja-table-container">
                    <div className="caja-table-header">
                        <div>Estado</div>
                        <div>Estafeta</div>
                        <div>Caja</div>
                        <div>Saldo</div>
                        <div>Acciones</div>
                    </div>
                    {filteredCajas.map((caja, index) => (
                        <CajaCard
                            key={index}
                            caja={caja}
                            isLoading={isLoading}
                            rotate={rotate}
                            handleCashTransaction={handleCashTransaction}
                            handleOpenCloseCash={handleOpenCloseCash}
                            handleReloadCashStatus={handleReloadCashStatus}
                            handleNewBalanceChange={handleNewBalanceChange}
                            newBalance={newBalance}
                            showAddWithdrawButtons={showAddWithdrawButtons}
                            roleBli={roleBli}
                            hasPendingRequest={pendingRequests[caja.IdCaja]}
                            viewType={viewType}
                        />
                    ))}
                </div>
            );
        }

        if (viewType === 'grid') {
            return (
                <div className="caja-grid-container">
                    {filteredCajas.map((caja, index) => (
                        <CajaCard
                            key={index}
                            caja={caja}
                            isLoading={isLoading}
                            rotate={rotate}
                            handleCashTransaction={handleCashTransaction}
                            handleOpenCloseCash={handleOpenCloseCash}
                            handleReloadCashStatus={handleReloadCashStatus}
                            handleNewBalanceChange={handleNewBalanceChange}
                            newBalance={newBalance}
                            showAddWithdrawButtons={showAddWithdrawButtons}
                            roleBli={roleBli}
                            hasPendingRequest={pendingRequests[caja.IdCaja]}
                            viewType={viewType}
                        />
                    ))}
                </div>
            );
        }

        // Vista línea (por defecto)
        return (
            <div className="caja-line-container">
                {filteredCajas.map((caja, index) => (
                    <CajaCard
                        key={index}
                        caja={caja}
                        isLoading={isLoading}
                        rotate={rotate}
                        handleCashTransaction={handleCashTransaction}
                        handleOpenCloseCash={handleOpenCloseCash}
                        handleReloadCashStatus={handleReloadCashStatus}
                        handleNewBalanceChange={handleNewBalanceChange}
                        newBalance={newBalance}
                        showAddWithdrawButtons={showAddWithdrawButtons}
                        roleBli={roleBli}
                        hasPendingRequest={pendingRequests[caja.IdCaja]}
                        viewType={viewType}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="caja-card-container">
            {renderViewControls()}
            
            {/* Sección de saldos individuales de estafetas - Solo visible para roleId === 9 */}
            {user?.roleId === 9 && (
                <div className="dashboardCard text-left" style={{ color: "black", marginBottom: "20px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Saldos por estafetas</h6>
                        <button 
                            className="btn btn-outline-blue d-flex align-items-center justify-content-center"
                            style={{ minWidth: 100, minHeight: 36, padding: "0 16px" }}
                            onClick={handleGetEstafetasData}
                            disabled={isLoadingEstafetas}
                        >
                            {isLoadingEstafetas ? (
                                <span style={{ display: "flex", alignItems: "center", height: 20 }}>
                                    <Spinner type={1} size={18} />
                                </span>
                            ) : (
                                "Actualizar"
                            )}
                        </button>
                    </div>
                    
                    {isLoadingEstafetas ? (
                        <div className="text-center py-4">
                            <Spinner type={1} size={30} color="#0eab9b" />
                            <p className="mt-2 text-muted">Cargando saldos de estafetas...</p>
                        </div>
                    ) : estafetasData.length > 0 ? (
                        <div 
                            className="d-flex gap-3 estafetas-scroll-container"
                            style={{
                                overflowX: "auto",
                                paddingBottom: "10px"
                            }}
                        >
                            {estafetasData
                                .filter(estafeta => 
                                    estafeta.Codigo?.toLowerCase().includes(filters.estafeta.toLowerCase()) ||
                                    estafeta.NombreSucursal?.toLowerCase().includes(filters.estafeta.toLowerCase())
                                )
                                .map((estafeta) => (
                                    <div 
                                        key={estafeta.IdSucursal}
                                        className="flex-shrink-0"
                                        style={{ minWidth: "180px" }}
                                    >
                                        <div 
                                            className="text-center p-3 h-100 estafeta-card"
                                            style={{
                                                background: "#f8f9fa",
                                                borderRadius: "8px",
                                                border: "1px solid #e9ecef",
                                                height: "120px",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between"
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.background = "#e9ecef";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.background = "#f8f9fa";
                                            }}
                                        >
                                            <div>
                                                <div className="mb-1">
                                                    <strong style={{ color: "#495057", fontSize: "0.9rem" }}>
                                                        {estafeta.Codigo || "N/A"}
                                                    </strong>
                                                </div>
                                                <div style={{ color: "#6c757d", fontSize: "0.75rem", lineHeight: "1.2" }}>
                                                    {estafeta.NombreSucursal || "Sin nombre"}
                                                </div>
                                            </div>
                                            <div className="mt-2">
                                                <span 
                                                    className="badge"
                                                    style={{
                                                        background: estafeta.SaldoSucursal > 0 ? "#d4edda" : "#f8d7da",
                                                        color: estafeta.SaldoSucursal > 0 ? "#155724" : "#721c24",
                                                        fontSize: "0.8rem",
                                                        padding: "4px 8px"
                                                    }}
                                                >
                                                    {formatCurrency(estafeta.SaldoSucursal || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-muted">No se encontraron datos de estafetas</p>
                        </div>
                    )}
                </div>
            )}
            
            {renderCajas()}
        </div>
    );
};

export default CajaCardContainer; 